<template>
  <div :class="!isModal && 'col-lg-3'">
    <rating-block
      :venue-id="venueId"
      :source-id="item.id"
      :label="item.label"
      @details-expand="handleDataLayerEvent('expand reviews summary')"
    />
  </div>
  <div class="reviews-block" :class="isModal ? 'reviews-block--modal' : 'col-lg-9'">
    <div class="reviews-block__container">
      <div class="reviews-block__reviews">
        <reviews-feed
          :items="item.reviews"
          :toggle-button-fn="onToggleResponseClick"
          :is-initial-loading="isLoading"
          :is-error="isError"
          :placeholders-number="limit"
          @refetch="refetch"
          @focusable-item-changed="onLastFocusableItemChanged"
        />
        <div v-if="showLoadMoreButton && isModal" class="reviews-block__btn">
          <base-button
            :label="t('reviews-load-more')"
            :is-loading="isFetching"
            variant="secondary"
            @click="loadMore"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-if="!isModal" class="col-lg-9 offset-lg-3">
    <div class="reviews-block__btn">
      <base-button
        v-if="showLoadMoreButton"
        :label="t('reviews-load-more')"
        :is-loading="isFetching"
        variant="secondary"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import BaseButton from '../BaseButton/BaseButton.vue';
import RatingBlock from './RatingBlock/RatingBlock.vue';
import ReviewsFeed from './ReviewsFeed/ReviewsFeed.vue';
import {
  useReputationReviewsQuery,
  fetchReputationReviewsResponseData,
} from '../../../utils/queries/useReputationQuery';
import { useButtonsAndLinksTracking } from '../../../data-layer/hooks/useButtonsAndLinksTracking.js';

const { t } = useI18n();

const { trackButtonState } = useButtonsAndLinksTracking();

const props = defineProps({
  venueId: {
    type: [String, Number],
    required: true,
  },
  limit: {
    type: Number,
    default: () => 5,
  },
  from: {
    type: String,
    default: () => null,
  },
  to: {
    type: String,
    default: () => null,
  },
  isModal: {
    type: Boolean,
    default: () => true,
  },
});

const GOOGLE_PLACES = {
  id: 'GOOGLE_PLACES',
  label: 'google_places',
  dl_component: 'google_widget',
};

const item = ref({
  ...GOOGLE_PLACES,
  reviews: [],
  reviewsOffset: 0,
  allReviewsLoaded: false,
});

const feedLastFocusableItem = ref(null);

const currentOffset = computed(() => item.value.reviewsOffset);
const showLoadMoreButton = computed(
  () => !!(item.value.reviews.length && !item.value.allReviewsLoaded)
);

const reputationQueryParams = computed(() => {
  const data = {
    venueId: props.venueId,
    sourceID: item.value.id,
    offset: currentOffset,
  };

  if (props.limit) data.limit = props.limit;
  if (props.from) data.from = dayjs(props.from).format('YYYY-MM-DD');
  if (props.to) data.to = dayjs(props.to).format('YYYY-MM-DD');

  return data;
});

const { data, isLoading, isFetching, isError, fetchNextPage, refetch } = useReputationReviewsQuery(
  reputationQueryParams.value
);

const onLastFocusableItemChanged = (item) => {
  feedLastFocusableItem.value = item;
};

const loadMore = () => {
  const last = feedLastFocusableItem.value;

  fetchNextPage().then(() => {
    last.focus();
  });

  handleDataLayerEvent('load more reviews');
};

const changeReviewValue = (reviewId, newValue) => {
  const reviews = item.value.reviews.map((review) => {
    if (review.id !== reviewId) return review;
    return { ...review, ...newValue };
  });

  item.value = { ...item.value, reviews };
};

const fetchResponses = async (review) => {
  const responses = await fetchReputationReviewsResponseData(review.id);
  if (responses?.length)
    changeReviewValue(review.id, {
      responses: responses,
      responsesLoading: false,
    });
};

const onToggleResponseClick = (review, isExpanded) => {
  const isResponsesLoadNeeded = review.hasResponses && !review.responses.length;

  changeReviewValue(review.id, {
    isExpanded: !isExpanded,
    responsesLoading: isResponsesLoadNeeded,
  });
  if (!isExpanded && isResponsesLoadNeeded) {
    fetchResponses(review);
    handleDataLayerEvent('load response');
  }
};

const removeDuplicateReviews = (reviews) => {
  return reviews.reduce((acc, review) => {
    if (!acc.some((item) => item.id === review.id)) acc.push(review);
    return acc;
  }, []);
};

const addReviewProps = (reviews) => {
  const MAX_COMMENT_LENGTH = 108;

  return reviews.map((review) => {
    const trimmedComment =
      review.comment.length > MAX_COMMENT_LENGTH
        ? review.comment.slice(0, MAX_COMMENT_LENGTH) + '...'
        : null;

    return {
      ...review,
      trimmedComment,
      responses: [],
      responsesLoading: false,
      isExpanded: !(trimmedComment || review.hasResponses),
    };
  });
};

const handleData = (data) => {
  const pages = data.value?.pages || [];

  const reviews =
    pages.reduce((acc, item) => {
      return item.reviews ? [...acc, ...item.reviews] : acc;
    }, []) || [];

  const uniqueReviews = removeDuplicateReviews(reviews);
  const mappedReviews = addReviewProps(uniqueReviews);

  const filteredReviews = mappedReviews.filter(
    (review) => !item.value.reviews.some((currentReview) => currentReview.id === review.id)
  );
  if (!filteredReviews.length) return;

  const allReviewsLoaded = !pages[pages.length - 1]?.paging.next;
  const allReviews = [...item.value.reviews, ...filteredReviews];

  item.value = {
    ...item.value,
    reviews: allReviews,
    reviewsOffset: allReviews.length,
    allReviewsLoaded,
  };
};

const handleDataLayerEvent = (action) => {
  trackButtonState(action, 'view_third_party_reviews', { component: item.value.dl_component });
};

onBeforeMount(() => {
  handleData(data);
});

watch(data, () => handleData(data));
</script>

<style lang="scss">
@import './scss/ReviewsBlock.scss';
</style>
